'use client'

import { useEffect, useRef, useState } from 'react'

import { FocusOn } from 'react-focus-on'

import cn from 'clsx'
import { Box } from '@/components/Box/Box'
import { Button } from '@/components/Button/Button'
import { Divider } from '@/components/Divider/Divider'
import { ChevronDown, ChevronUp } from '@doist/icons/brand'

import css from './bottom-sheet.module.css'

export type BottomSheetProps = {
    children: React.ReactNode
    title: string
    onOpen?: () => void
    onClose?: () => void
}

export function BottomSheet({ children, title, onOpen, onClose }: BottomSheetProps) {
    const targetRef = useRef<HTMLElement>(null)
    const [isOpen, setOpen] = useState(false)

    const toggle = () => {
        if (isOpen) {
            onClose?.()
        } else {
            onOpen?.()
        }

        setOpen(!isOpen)
    }

    useEffect(() => {
        if (!isOpen) {
            return
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.code === 'Escape') {
                onClose?.()
                setOpen(false)
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [onClose, isOpen])

    return (
        <FocusOn enabled={isOpen}>
            <Box ref={targetRef}>
                <Box className={cn(css.overlay, { [css.opened]: isOpen })} onClick={toggle} />
                <Box className={cn(css.bottomSheet, { [css.opened]: isOpen })}>
                    <Button
                        variant="quaternary"
                        startIcon={isOpen ? ChevronDown : ChevronUp}
                        size="large"
                        onClick={toggle}
                        className={css.button}
                    >
                        {title}
                    </Button>
                    <Divider />
                    <Box className={css.contents}>{children}</Box>
                </Box>
            </Box>
        </FocusOn>
    )
}
